import { Injectable, Injector } from '@angular/core';
import { from } from 'rxjs';
import { forkJoin } from "rxjs/observable/forkJoin";
import { Observable } from 'rxjs';
import { BaseEditService, SchedulerModelFields, SchedulerModule } from '@progress/kendo-angular-scheduler';
import { parseDate } from '@progress/kendo-angular-intl';
import {
    EngineerScheduleService,
    HolidayScheduleService,
    WorkOrderScheduleService
} from '../ve.service';
import { AuthenticationService } from '../../../auth.service';


const fields: SchedulerModelFields = (
    {
        id: 'Id',
        title: 'Description',
        start: 'PlannedStartDate',
        end: 'PlannedCompletionDate',
        engineer: 'Engineer',
    } as SchedulerModule);

@Injectable()
export class EngineerScheduleEditService extends BaseEditService<Event> {
    public programsLoading = false;
    public workOrderSearchLoading = false;
    public loading = false;
    public userProfile;
    public readonly engineerSchedulerService: EngineerScheduleService;
    public readonly holidayService: HolidayScheduleService;
    public readonly workOrderSchedulService: WorkOrderScheduleService;
    public readonly authService: AuthenticationService;

    constructor(
        injector: Injector
    ) {
        super(fields);
        this.engineerSchedulerService = injector.get(EngineerScheduleService);
        this.holidayService = injector.get(HolidayScheduleService);
        this.workOrderSchedulService = injector.get(WorkOrderScheduleService);
        this.authService = injector.get(AuthenticationService);
    }

    /**
     * Scheduler overwritten read function to return ve defined data to the scheduler.
     *
     * @returns {void}
     * @memberof EditService
     */
    public read(): void {
        this.fetch().subscribe(data => {
            this.getHolidays(data);
            this.getWorkOrders(data);
            const result = data[0].concat(data[1], data[2]);
            this.data = result.map(item => this.readEvent(item));
            this.source.next(this.data);
            this.loading = false;
        });
    }

    /**
    * Scheduler overwritten method for saving. Updating, creating, and deleting types are overwritten
    * to use VE service endpoints.
    *
    * @protected
    * @param {Event[]} created
    * @param {Event[]} updated
    * @param {Event[]} deleted
    * @memberof EditService
    */
    protected save(created: Event[], updated: Event[], deleted: Event[]): void {
        if (deleted.length) {
            this.loading = true;
            this.engineerSchedulerService.remove(`/${deleted[0]['Id']}`)
                .then(() => {
                    this.read();
                });
        }

        if (updated.length) {
            this.loading = true;
            this.setDateTime(updated);
            this.engineerSchedulerService.save(updated[0], false, `/${updated[0]['Id']}`)
                .then(() => {
                    this.read();
                });
        }

        if (created.length) {
            this.loading = true;
            this.setDateTime(created);
            created[0]['Engineer'] = this.userProfile.GMIN;
            this.engineerSchedulerService.save(created[0], true)
                .then(() => {
                    this.read();
                });
        }

    }

    /**
     * Defines the standard 8-5 hours for the planned starting and ending dates.
     *
     * @param {*} dataItem
     * @memberof WorkOrderScheduleEditService
     */
    setDateTime(dataItem: any) {
        const planedStartTime = new Date(dataItem[0]['PlannedStartDate']);
        planedStartTime.setHours(3, 0, 0, 0);
        dataItem[0]['PlannedStartDate'] = planedStartTime;

        const planedEndTime = new Date(dataItem[0]['PlannedCompletionDate']);
        planedEndTime.setHours(12, 0, 0, 0);
        dataItem[0]['PlannedCompletionDate'] = planedEndTime;
    }

    /**
     * Scheduler overwritten method for calling ve endpoints to retrieve scheduler data objects.
     *
     * @protected
     * @param {string} [action='']
     * @param {*} [data]
     * @returns {Observable<any[]>}
     * @memberof EditService
     */
    protected fetch(): Observable<any[]> {
        this.loading = true;
        return from(this.getEvents());
    }

    /**
     * Mapper for creating scheduler events defined by SchedulerModelFields type defined above.
     *
     * @private
     * @param {*} item
     * @returns {Event}
     * @memberof WorkOrderScheduleEditService
     */
    private readEvent(item: any): Event {
        return {
            ...item,
            PlannedStartDate: item.isHoliday ?
                this.setHolidayStartDateTime(item) :
                parseDate(item.PlannedStartDate),
            PlannedCompletionDate: item.isHoliday ?
                this.setHolidayEndDateTime(item) :
                parseDate(item.PlannedCompletionDate),
        };
    }

    /**
     * Get all of the endpoints needed for rendering events and return them as one object.
     * Each event type is an array in the object returned in the order called here.
     * @returns
     * @memberof WorkOrderScheduleEditService
     */
    getEvents() {
        return forkJoin([
            this.holidayService.read('/?activeOnly=true'),
            this.workOrderSchedulService.read(`/assignedEngineer/${this.userProfile.GMIN}/${this.authService.getModule()}`),
            this.engineerSchedulerService.read(`/engineer/${this.userProfile.GMIN}`),
        ]);
    }

    /**
     * Get holidays from 0 element of data array and map to event model.
     *
     * @param {*} data
     * @memberof WorkOrderScheduleEditService
     */
    getHolidays(data) {
        data[0].forEach(element => {
            element.Description = `${element.GlobalRegionDescription} Holiday`;
            element.engineer = null;
            element.isHoliday = true;
        });
    }

    getWorkOrders(data) {
        data[1].forEach(element => {
            element.Description = `(${element.WorkOrderStatus}) ${element.WorkOrderNumber} ${element.StudyTitle}`;
            element.engineer = element.AssignedEngineer;
            element.isWorkOrder = true;
        });
    }

    getVacations(data) {
        data[2].forEach(element => {
            element.isVacation = true;
        });
    }

    setHolidayStartDateTime(dataItem: any) {
        const planedStartTime = new Date(dataItem.HolidayDate);
        planedStartTime.setHours(9, 0, 0, 0);
        return planedStartTime;
    }

    setHolidayEndDateTime(dataItem: any) {
        const planedEndTime = new Date(dataItem.HolidayDate);
        planedEndTime.setHours(16, 0, 0, 0);
        return planedEndTime;
    }
}
